import { lazy } from "nodash"
import { z } from "zod"
import {
    ColorFinderFrameCommentContext,
    ColorFinderCommentReplyContext,
    colorFinderFrameCommentContextSchema,
    colorFinderCommentReplyContextSchema,
} from "./colorfinder"
import {
    MapPinCommentContext,
    MapCommentReplyContext,
    mapPinCommentContextSchema,
    mapCommentReplyContextSchema,
} from "./map-comment"
import { CommentReactionSummary } from "./comment-reaction"

export const commentContextSchema = z.discriminatedUnion("target", [
    colorFinderFrameCommentContextSchema,
    colorFinderCommentReplyContextSchema,
    mapCommentReplyContextSchema,
    mapPinCommentContextSchema,
])

export const MAX_COMMENT_LENGTH = 5000
export const MAX_COMMENT_IMAGES = 10

export const commentImageFileIdSchema = z.string().regex(/^[A-Za-z0-9._-]+$/)

export const MAX_REACTION_EMOJI_LENGTH = 16

const EMOJI_START = /\p{Extended_Pictographic}|\p{Regional_Indicator}|[0-9#*]\uFE0F?\u20E3/u

const graphemeSegmenter = lazy(() => new Intl.Segmenter("en", { granularity: "grapheme" }))

const isSingleEmojiCluster = (value: string) =>
    [...graphemeSegmenter().segment(value)].length === 1 && EMOJI_START.test(value)

export const emojiSchema = z
    .string()
    .min(1)
    .max(MAX_REACTION_EMOJI_LENGTH)
    .refine(isSingleEmojiCluster)

type CommentTarget = "cf-frame" | "cf-comment" | "map-pin" | "map-comment"

interface CommentContextMap extends Record<CommentTarget, unknown> {
    "cf-frame": ColorFinderFrameCommentContext
    "cf-comment": ColorFinderCommentReplyContext
    "map-pin": MapPinCommentContext
    "map-comment": MapCommentReplyContext
}

interface Comment<T extends CommentTarget = CommentTarget> {
    id: string
    comment: string
    ownerId: string
    context: CommentContextMap[T]
    mentionedUserIds: string[]
    imageKeys: string[]
    numReplies: number
    isUnread: boolean
    participantIds: string[]
    reactions: CommentReactionSummary[]
    createdBy: string
    createdAt: number
    entityId: string
    deletion?: {
        deletedBy: string
        deletedAt: number
    }
    update?: {
        updatedBy: string
        updatedAt: number
    }
}

interface CreateableComment<T extends CommentTarget = CommentTarget> {
    comment: string
    entityId: string
    context: CommentContextMap[T]
    imageKeys: string[]
    userId: string
    ownerId: string
}

const UUID = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}"

const MENTION_TOKEN = new RegExp(`(\\\\*)@\\[user:(${UUID})\\]`, "gi")

const TOKEN_LOOKALIKE = /(@\[user:|\\)/g

const escapeCommentText = (text: string): string =>
    text.replace(TOKEN_LOOKALIKE, (match) => `\\${match}`)

const unescapeCommentText = (text: string): string => text.replace(/\\(.)/g, "$1")

const URL_TOKEN = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi

const TRAILING_PUNCTUATION = /[.,;:!?)\]}'"]+$/

const LINK_TOKEN = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g

const splitBareUrls = (value: string): CommentSegment[] => {
    const segments: CommentSegment[] = []
    let lastIndex = 0
    for (const match of value.matchAll(URL_TOKEN)) {
        const rawUrl = match[0]
        const trimmed = rawUrl.replace(TRAILING_PUNCTUATION, "")
        if (!trimmed) continue
        const start = match.index
        const end = start + trimmed.length
        if (start > lastIndex) {
            segments.push({
                type: "text",
                value: unescapeCommentText(value.slice(lastIndex, start)),
            })
        }
        const bare = unescapeCommentText(trimmed)
        const url = bare.startsWith("www.") ? `https://${bare}` : bare
        segments.push({ type: "link", url, label: bare })
        lastIndex = end
    }
    if (lastIndex < value.length) {
        segments.push({ type: "text", value: unescapeCommentText(value.slice(lastIndex)) })
    }
    return segments
}

const splitLinks = (value: string): CommentSegment[] => {
    const segments: CommentSegment[] = []
    let lastIndex = 0
    for (const match of value.matchAll(LINK_TOKEN)) {
        if (match.index > lastIndex) {
            segments.push(...splitBareUrls(value.slice(lastIndex, match.index)))
        }
        segments.push({
            type: "link",
            label: unescapeCommentText(match[1]),
            url: match[2],
        })
        lastIndex = match.index + match[0].length
    }
    if (lastIndex < value.length) {
        segments.push(...splitBareUrls(value.slice(lastIndex)))
    }
    return segments
}

type CommentSegment =
    | { type: "text"; value: string }
    | { type: "mention"; userId: string }
    | { type: "link"; url: string; label: string }

const buildMentionToken = (userId: string): string => `@[user:${userId}]`

const buildLinkToken = (label: string, url: string): string => `[${label}](${url})`

const insertText = (value: string, text: string, caretIndex?: number): string => {
    const at = caretIndex ?? value.length
    const cut = Math.max(0, Math.min(at, value.length))
    return `${value.slice(0, cut)}${escapeCommentText(text)}${value.slice(cut)}`
}

const isEscaped = (escapes: string): boolean => escapes.length % 2 === 1

const extractMentionedUserIds = (text: string): string[] => {
    const ids = new Set<string>()
    for (const match of text.matchAll(MENTION_TOKEN)) {
        if (isEscaped(match[1])) continue
        ids.add(match[2].toLowerCase())
    }
    return [...ids]
}

const enrichCommentMentions = (
    text: string,
    replace: (mention: { rawMention: string; userId: string }) => string
): string =>
    text.replace(MENTION_TOKEN, (rawMention, escapes: string, userId: string) =>
        isEscaped(escapes)
            ? rawMention
            : `${escapes}${replace({
                  rawMention: rawMention.slice(escapes.length),
                  userId: userId.toLowerCase(),
              })}`
    )

const parseCommentSegments = (text: string): CommentSegment[] => {
    const segments: CommentSegment[] = []
    let lastIndex = 0
    for (const match of text.matchAll(MENTION_TOKEN)) {
        if (isEscaped(match[1])) continue
        const start = match.index + match[1].length
        if (start > lastIndex) {
            segments.push(...splitLinks(text.slice(lastIndex, start)))
        }
        segments.push({ type: "mention", userId: match[2].toLowerCase() })
        lastIndex = match.index + match[0].length
    }
    if (lastIndex < text.length) {
        segments.push(...splitLinks(text.slice(lastIndex)))
    }
    return segments
}

const COMMENT_SNIPPET_MAX_LENGTH = 140

const ATOMIC_TOKEN = new RegExp(`${MENTION_TOKEN.source}|${LINK_TOKEN.source}|\\\\.`, "gi")

const snippetCutoff = (text: string): number => {
    for (const match of text.matchAll(ATOMIC_TOKEN)) {
        const start = match.index
        if (start >= COMMENT_SNIPPET_MAX_LENGTH) break
        if (start + match[0].length > COMMENT_SNIPPET_MAX_LENGTH) return start
    }
    return COMMENT_SNIPPET_MAX_LENGTH
}

const commentSnippet = (text: string): string =>
    text.length > COMMENT_SNIPPET_MAX_LENGTH
        ? `${text.slice(0, snippetCutoff(text)).trimEnd()}…`
        : text

export {
    buildLinkToken,
    buildMentionToken,
    commentSnippet,
    enrichCommentMentions,
    escapeCommentText,
    extractMentionedUserIds,
    insertText,
    parseCommentSegments,
    unescapeCommentText,
}
export type { Comment, CommentContextMap, CommentSegment, CommentTarget, CreateableComment }
